import { Button, Card, Input, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Room = {
  id: string;
  code: string;
  name: string;
  capacity?: number | null;
  campus_name?: string;
  is_active?: number | boolean;
};

export default function TimetableRoomsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-timetable-rooms', q],
    queryFn: async () => {
      const res = await api.get<Room[]>('/education/timetable/rooms', { params: { q: q || undefined } });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('timetableRooms')}
      description={t('timetableRoomsIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/timetable/rooms/new')}>
              {t('newRoom')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/timetable')}>{t('backToTimetable')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Space>
            <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear placeholder={t('search')} style={{ width: 220 }} />
            <Button onClick={() => list.refetch()} loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Space>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              { title: t('code'), dataIndex: 'code', width: 120 },
              { title: t('name'), dataIndex: 'name' },
              { title: t('campus'), dataIndex: 'campus_name', render: (v) => v || '—' },
              { title: t('capacity'), dataIndex: 'capacity', width: 90, render: (v) => (v != null ? v : '—') },
              {
                title: t('status'),
                width: 90,
                render: (_: unknown, row: Room) =>
                  row.is_active === 0 || row.is_active === false ? (
                    <Tag>{t('inactive')}</Tag>
                  ) : (
                    <Tag color="success">{t('active')}</Tag>
                  ),
              },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 100,
                      render: (_: unknown, row: Room) => (
                        <Button size="small" onClick={() => navigate(`/education/timetable/rooms/${row.id}/edit`)}>
                          {t('edit')}
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
